import type { Inventory } from '../inventory/inventory.js';

export interface BuildingCost {
  itemId: string;
  name: string;
  quantity: number;
}

export interface BuildingBlueprint {
  type: string;
  name: string;
  description: string;
  costs: readonly BuildingCost[];
  widthTiles: number;
  depthTiles: number;
}

export interface PlacedStructure {
  id: string;
  type: string;
  name: string;
  gridX: number;
  gridZ: number;
  createdAt: number;
}

export interface SettlementGridOptions {
  width?: number;
  depth?: number;
  cellSizeMeters?: number;
}

export const BUILDING_BLUEPRINTS: Record<string, BuildingBlueprint> = {
  campfire: {
    type: 'campfire',
    name: 'Campfire',
    description: 'Central hearth providing warmth and rallying point for monsters.',
    costs: [{ itemId: 'herb_vitality', name: 'Vitality Herb', quantity: 4 }],
    widthTiles: 1,
    depthTiles: 1
  },
  storage_shed: {
    type: 'storage_shed',
    name: 'Storage Shed',
    description: 'Secures items and increases settlement stash capacity.',
    costs: [{ itemId: 'iron_ore', name: 'Iron Ore', quantity: 3 }],
    widthTiles: 1,
    depthTiles: 1
  },
  slime_pen: {
    type: 'slime_pen',
    name: 'Slime Shelter Pen',
    description: 'Cozy habitat that boosts companion comfort and work productivity.',
    costs: [
      { itemId: 'iron_ore', name: 'Iron Ore', quantity: 2 },
      { itemId: 'herb_vitality', name: 'Vitality Herb', quantity: 2 }
    ],
    widthTiles: 1,
    depthTiles: 1
  }
};

export class SettlementGrid {
  public readonly width: number;
  public readonly depth: number;
  public readonly cellSizeMeters: number;

  private structures = new Map<string, PlacedStructure>();

  constructor(options: SettlementGridOptions = {}) {
    this.width = options.width ?? 16;
    this.depth = options.depth ?? 16;
    this.cellSizeMeters = options.cellSizeMeters ?? 2.0;
  }

  private getKey(x: number, z: number): string {
    return `${x},${z}`;
  }

  public isWithinBounds(x: number, z: number): boolean {
    const halfW = Math.floor(this.width / 2);
    const halfD = Math.floor(this.depth / 2);
    return x >= -halfW && x <= halfW && z >= -halfD && z <= halfD;
  }

  public getStructureAt(gridX: number, gridZ: number): PlacedStructure | undefined {
    return this.structures.get(this.getKey(gridX, gridZ));
  }

  public getAllStructures(): readonly PlacedStructure[] {
    return Array.from(this.structures.values());
  }

  public canBuild(
    buildingType: string,
    gridX: number,
    gridZ: number,
    inventory: Inventory
  ): { allowed: boolean; reason?: string } {
    const blueprint = BUILDING_BLUEPRINTS[buildingType];
    if (!blueprint) {
      return { allowed: false, reason: `Unknown building blueprint: ${buildingType}` };
    }

    if (!this.isWithinBounds(gridX, gridZ)) {
      return { allowed: false, reason: 'Location is out of settlement bounds' };
    }

    if (this.structures.has(this.getKey(gridX, gridZ))) {
      return { allowed: false, reason: 'Tile is already occupied by another structure' };
    }

    for (const cost of blueprint.costs) {
      if (!inventory.hasItem(cost.itemId, cost.quantity)) {
        return {
          allowed: false,
          reason: `Insufficient ${cost.name}: requires ${cost.quantity}, has ${inventory.getItemCount(cost.itemId)}`
        };
      }
    }

    return { allowed: true };
  }

  public build(
    buildingType: string,
    gridX: number,
    gridZ: number,
    inventory: Inventory
  ): PlacedStructure | null {
    const check = this.canBuild(buildingType, gridX, gridZ, inventory);
    if (!check.allowed) {
      return null;
    }

    const blueprint = BUILDING_BLUEPRINTS[buildingType];

    // Deduct cost
    for (const cost of blueprint.costs) {
      inventory.removeItem(cost.itemId, cost.quantity);
    }

    const structure: PlacedStructure = {
      id: `struct_${buildingType}_${gridX}_${gridZ}`,
      type: buildingType,
      name: blueprint.name,
      gridX,
      gridZ,
      createdAt: Date.now()
    };

    this.structures.set(this.getKey(gridX, gridZ), structure);
    return structure;
  }
}
